/**
 * @author: Richard Scoones @ 29/11/2018
 * @description: GET and POST data securely to session server and cross tenants
 */
import {omit} from "lodash"
import API from "@irati/utils/services/API"
import {getDomain} from "@irati/utils/links"
import PostForm from "@irati/utils/PostForm"
import setIfNotSet from "@irati/utils/setIfNotSet"
import codes from "../errorCodes/Session"

const endpoint = "/api/v1/session-data"

interface SessionData {
  [key: string]: any
}

interface Options {
  target?: string,
  pathname?: string
}

class Session {
  get(options = {}): Promise<SessionData> {
    return API.get(endpoint, options).then((res) => res.data)
  }

  post(data: SessionData, options = {}): Promise<SessionData> {
    setIfNotSet(options, "addCSRF", true)
    API.sentryIgnoreError(options, [codes.EBADCSRFTOKEN])
    return API.post(endpoint, data, options).then((res) => res.data)
  }

  postCrossDomain(
    targetSubDomain: string,
    data: SessionData = {},
    options: Options = {},
    autoSubmit: boolean = true
  ): Promise<HTMLFormElement> {
    const url = getDomain(targetSubDomain)
    const {pathname = "", target = ""} = options
    const form = PostForm(`${url}${pathname}`, data, target)
    if (autoSubmit) {
      form.submit()
    }
    return Promise.resolve(form)
  }

  /**
   * @description: Get SessionData then remove the provided keys.
   *               Useful helper function to get one time values such as email on login.
   */
  getThenRemove(keys: string[], options = {}): Promise<SessionData> {
    return this.get(options).then((data) => {
      const filtered = omit(data, keys)
      return this.post(filtered, options).then(() => {
        // return original
        return data
      })
    })
  }
}

const session = new Session()

export const getSessionData = session.get
export const postSessionData = session.post
export const postCrossDomainSessionData = session.postCrossDomain

export default session
